<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { Query } from '@/generated/graphql'
import { BaseValidation } from '@vuelidate/core'
import gql from 'graphql-tag'
import { computed, ref } from 'vue'
import FormField from '@/components/input/FormField.vue'
import UploadPicker, { PickedUpload } from '@/components/upload/UploadPicker.vue'
import useContentTypes from '@/composables/useContentTypes'

const props = defineProps<{
  modelValue?: string
  label: string
  validation?: BaseValidation
  required?: boolean
}>()
const emit = defineEmits<{
  'update:modelValue': [value: string | undefined]
}>()

const imageMaxDimension = 75
const id = computed(() => props.modelValue)
const fetchQuery = useQuery<Query>(
  gql`
    query getUploadUrlForImage($id: ID!, $maxDimension: Int!) {
      upload {
        upload(id: $id) {
          id
          imageUrl(options: { width: $maxDimension, height: $maxDimension })
        }
      }
    }
  `,
  () => ({
    id: id.value,
    maxDimension: imageMaxDimension,
  }),
  () => ({
    enabled: !!id.value,
  }),
)
const upload = computed(() => fetchQuery.result.value?.upload.upload)

const uploadPickerOpen = ref(false)
function selectUpload(upload: PickedUpload) {
  emit('update:modelValue', upload.id)
}

const { contentTypesImage } = useContentTypes()
</script>

<template>
  <form-field :label="props.label" :validation="props.validation" :required="props.required">
    <div class="d-flex align-center">
      <v-btn icon="edit" class="mr-5" @click="uploadPickerOpen = true" />
      <template v-if="!!upload && !!props.modelValue">
        <div>
          <v-img
            :src="upload.imageUrl"
            :height="`${imageMaxDimension}px`"
            :width="`${imageMaxDimension}px`"
            class="text-center align-center"
          >
            <v-btn
              variant="outlined"
              color="white"
              icon="delete"
              density="compact"
              @click="emit('update:modelValue', undefined)"
            />
          </v-img>
        </div>
      </template>
    </div>
  </form-field>
  <upload-picker
    v-model="uploadPickerOpen"
    pick-one
    :mime-types="contentTypesImage"
    @pick-one="selectUpload"
  />
</template>

<style scoped lang="scss"></style>
